import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { cache } from '../lib/cache';
import { calculateAge, parseAgeFilter, evaluateAgeCondition } from '../lib/utils';

export interface DataTableOptions {
  tableName: string;
  schema?: string;
  whereClause?: string;
  columns?: string[];
  virtualColumns?: string[];
  sortable?: boolean;
  searchable?: boolean;
  filterable?: boolean;
  selectable?: boolean;
  pageSize?: number;
  initialPage?: number;
  limit?: number;
  offset?: number;
  initialData?: any[];
  adminMode?: boolean;
}

export function useDataTable({
  tableName,
  schema,
  whereClause,
  columns = [],
  virtualColumns = [],
  sortable = true,
  searchable = true,
  filterable = true,
  selectable = true,
  pageSize: initialPageSize = 1000,
  initialPage = 1,
  limit,
  offset,
  initialData,
  adminMode = false,
}: DataTableOptions) {
  const [data, setData] = useState<any[]>(initialData || []);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<any>(null);

  // Sincronizza lo stato data se initialData cambia esternamente
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setData(initialData);
      setTotalCount(initialData.length);
      setLoading(false);
    }
  }, [initialData]);

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, any[]>>({});
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(new Set());
  const [columnValues, setColumnValues] = useState<Record<string, any[]>>({});
  
  // Pagination state - Use limit/offset if provided as preferred initial values
  const [page, setPage] = useState(() => {
    if (offset !== undefined && limit !== undefined && limit > 0) {
      return Math.floor(offset / limit) + 1;
    }
    return initialPage;
  });
  const [pageSize, setPageSize] = useState(limit || initialPageSize);
  const [totalCount, setTotalCount] = useState(0);

  const columnsKey = JSON.stringify(columns);
  const virtualColumnsKey = JSON.stringify(virtualColumns);
  const sortConfigKey = JSON.stringify(sortConfig);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filters]);

  const lastFetchParams = useRef<string>('');

  const fetchData = useCallback(async (invalidateCache: boolean = false) => {
    // If we have initialData and haven't explicitly requested a refresh, skip fetching
    if (initialData && !invalidateCache && data.length > 0 && lastFetchParams.current === '') {
       setLoading(false);
       return;
    }

    const currentParams = JSON.stringify({ 
      tableName, schema, whereClause, columnsKey, virtualColumnsKey, sortConfigKey, page, pageSize, searchTerm 
    });
    
    // Prevent redundant fetches if params haven't changed, 
    // but allow forced invalidation (e.g. refresh button)
    if (!invalidateCache && lastFetchParams.current === currentParams) {
      return;
    }
    
    lastFetchParams.current = currentParams;
    setLoading(true);
    try {
      const cacheKey = `data_${schema || 'public'}_${tableName}_${page}_${pageSize}_${sortConfigKey}_${whereClause || ''}`;
      
      if (!invalidateCache && !searchTerm) { // Only use cache if not searching
        const cachedResult = cache.get<{ data: any[], count: number, columnValues: Record<string, any[]> }>(cacheKey);
        if (cachedResult) {
          setData(cachedResult.data);
          setTotalCount(cachedResult.count);
          setColumnValues(cachedResult.columnValues);
          setLoading(false);
          return;
        }
      }

      const parsedColumns: string[] = JSON.parse(columnsKey);
      const parsedVirtualColumns: string[] = JSON.parse(virtualColumnsKey);
      
      const selectableColumns = parsedColumns.filter(c => !parsedVirtualColumns.includes(c));
      let querySource = schema ? supabase.schema(schema) : supabase;
      let query = querySource
        .from(tableName)
        .select(selectableColumns.length > 0 ? selectableColumns.join(',') : '*', { count: 'exact' });

      // Add whereClause conditions
      if (whereClause) {
        const conditions = whereClause.split(/\s+AND\s+/i);
        conditions.forEach(cond => {
          const match = cond.match(/(\w+)\s*(=|LIKE|!=|<>|>|<|>=|<=)\s*(.*)/i);
          if (match) {
            let [, col, op, val] = match;
            val = val.trim().replace(/^'|'$/g, '');
            
            let operator = 'eq';
            const opUpper = op.toUpperCase();
            if (opUpper === 'LIKE') operator = 'like';
            else if (op === '!=') operator = 'neq';
            else if (op === '<>') operator = 'neq';
            else if (op === '>') operator = 'gt';
            else if (op === '<') operator = 'lt';
            else if (op === '>=') operator = 'gte';
            else if (op === '<=') operator = 'lte';
            
            query = query.filter(col, operator as any, val);
          }
        });
      }

      // Add server-side search if needed (simple implementation for text columns)
      // Note: This is an optimization, we still filter locally for full coverage
      
      if (sortConfig) {
        query = query.order(sortConfig.key, { ascending: sortConfig.direction === 'asc' });
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      let result: any[] = [];
      let count: number | null = null;
      let fetchError: any = null;

      if (adminMode) {
        // Use API Proxy to bypass RLS
        const orderKey = sortConfig ? sortConfig.key : 'id';
        const orderDir = sortConfig ? sortConfig.direction : 'asc';
        const resp = await fetch(`/api/sys/data/${tableName}?limit=${pageSize}&offset=${from}&sort=${orderKey}&dir=${orderDir}`);
        if (!resp.ok) {
          const err = await resp.json();
          fetchError = new Error(err.error || "Errore nel fetch proxy");
          console.error("ADMIN FETCH FAIL:", err);
        } else {
          const proxyData = await resp.json();
          result = proxyData.data || [];
          count = proxyData.count || 0;
        }
      } else {
        query = query.range(from, to);
        const { data: resData, error: resErr, count: resCount } = await query;
        result = resData || [];
        count = resCount;
        fetchError = resErr;
      }

      if (fetchError) {
        throw fetchError;
      }
      setData(result || []);
      if (count !== null) setTotalCount(count);

      let uniqueValues: Record<string, any[]> = {};
      if (filterable && result && result.length > 0) {
        const cols = parsedColumns.length > 0 ? parsedColumns : Object.keys(result[0] || {});
        cols.forEach(col => {
          const values = Array.from(new Set(result.map(row => row[col]))).filter(v => v !== null && v !== undefined);
          uniqueValues[col] = values;
        });
        setColumnValues(uniqueValues);
      }

      cache.set(cacheKey, { 
        data: result || [], 
        count: count || 0, 
        columnValues: uniqueValues 
      }, 0.083);
    } catch (err: any) {
      console.error(`Error fetching data from ${tableName}:`, err);
      setError(err);
      const isMissingTable = err?.message?.includes('schema cache') || 
                             err?.message?.includes('Could not find the table') ||
                             err?.code === '42P01';
      if (!isMissingTable) {
        toast.error(`Errore nel caricamento dati da ${tableName}`);
      }
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [tableName, columnsKey, virtualColumnsKey, sortConfigKey, filterable, schema, whereClause, page, pageSize, searchTerm]);


  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Realtime subscription - only for public schema
  useEffect(() => {
    if (schema && schema !== 'public') return;

    // Use a unique channel name to prevent "cannot add callbacks after subscribe()" 
    // errors when the component remounts rapidly or dependencies change
    const channelName = `${tableName}-changes-${Math.random().toString(36).substring(2, 9)}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: tableName },
        (payload) => {
          fetchData(); // Simple approach: refetch everything
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tableName, schema, fetchData]);

  const insertRow = useCallback(async (newData: any) => {
    try {
      if (adminMode) {
        const resp = await fetch(`/api/sys/data/${tableName}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newData)
        });
        if (!resp.ok) {
           const err = await resp.json();
           throw new Error(err.error || 'Errore inserimento proxy');
        }
        const result = await resp.json();
        toast.success('Riga inserita con successo');
        return result;
      }
      
      let client = schema ? supabase.schema(schema) : supabase;
      const { data: result, error: insertError } = await client
        .from(tableName)
        .insert(newData)
        .select()
        .single();
      if (insertError) throw insertError;
      toast.success('Riga inserita con successo');
      return result;
    } catch (err: any) {
      toast.error(`Errore nell'inserimento: ${err.message}`);
      throw err;
    }
  }, [tableName, schema]);

  const updateRow = useCallback(async (id: string | number, updatedData: any) => {
    try {
      if (adminMode) {
        let idField = 'id';
        if (updatedData && typeof updatedData === 'object') {
          if ('id' in updatedData) idField = 'id';
          else if ('table_name' in updatedData) idField = 'table_name';
          else if ('item' in updatedData) idField = 'item';
          else if ('name' in updatedData) idField = 'name';
          else if ('progressivo' in updatedData) idField = 'progressivo';
        }
        
        const resp = await fetch(`/api/sys/data/${tableName}/${id}?idField=${idField}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedData)
        });
        if (!resp.ok) {
           const err = await resp.json();
           throw new Error(err.error || 'Errore aggiornamento proxy');
        }
        const result = await resp.json();
        toast.success('Riga aggiornata con successo');
        return result;
      }

      let client = schema ? supabase.schema(schema) : supabase;
      const { data: result, error: updateError } = await client
        .from(tableName)
        .update(updatedData)
        .eq('id', id)
        .select()
        .single();
      if (updateError) throw updateError;
      toast.success('Riga aggiornata con successo');
      return result;
    } catch (err: any) {
      toast.error(`Errore nell'aggiornamento: ${err.message}`);
      throw err;
    }
  }, [tableName, schema]);

  const deleteRows = useCallback(async (ids: (string | number)[]) => {
    try {
      if (adminMode) {
        let idField = 'id';
        if (data.length > 0) {
            const sample = data[0];
            if ('id' in sample) idField = 'id';
            else if ('table_name' in sample) idField = 'table_name';
            else if ('item' in sample) idField = 'item';
            else if ('name' in sample) idField = 'name';
            else if ('progressivo' in sample) idField = 'progressivo';
        }
        for (const id of ids) {
          const resp = await fetch(`/api/sys/data/${tableName}/${id}?idField=${idField}`, {
            method: 'DELETE'
          });
          if (!resp.ok) {
            const err = await resp.json();
            throw new Error(err.error || 'Errore eliminazione proxy');
          }
        }
        setSelectedRows(new Set());
        toast.success(`${ids.length} righe eliminate`);
        return;
      }
      let client = schema ? supabase.schema(schema) : supabase;
      const { error: deleteError } = await client
        .from(tableName)
        .delete()
        .in('id', ids);
      if (deleteError) throw deleteError;
      setSelectedRows(new Set());
      toast.success(`${ids.length} righe eliminate`);
    } catch (err: any) {
      toast.error(`Errore nell'eliminazione: ${err.message}`);
      throw err;
    }
  }, [tableName, schema]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const filteredData = useMemo(() => {
    const ageFilter = searchTerm && searchTerm.trim().length >= 2 ? parseAgeFilter(searchTerm) : null;

    return data.filter(row => {
      // Search - supporta filtro per età (es. "> 65", "< 35", "<> 80") o testo
      if (ageFilter) {
        const birthVal = row.data_nascita || row.data_di_nascita || row.birth_date;
        const age = calculateAge(birthVal);
        if (!evaluateAgeCondition(age, ageFilter.op, ageFilter.targetAge)) {
          return false;
        }
      } else if (searchTerm && searchTerm.length >= 2) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = Object.values(row).some(val => 
          String(val).toLowerCase().includes(searchLower)
        );
        if (!matchesSearch) return false;
      }

      // Filters
      for (const [col, selectedValues] of Object.entries(filters)) {
        const values = selectedValues as any[];
        if (values.length > 0 && !values.includes(row[col])) {
          return false;
        }
      }

      return true;
    });
  }, [data, searchTerm, filters]);

  return useMemo(() => ({
    data: filteredData,
    rawData: data,
    loading,
    error,
    fetchData,
    insertRow,
    updateRow,
    deleteRows,
    sortConfig,
    setSortConfig,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    selectedRows,
    setSelectedRows,
    columnValues,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    options: { sortable, searchable, filterable, selectable }
  }), [
    filteredData, data, loading, error, fetchData, insertRow, updateRow, deleteRows,
    sortConfig, setSortConfig, searchTerm, setSearchTerm, filters, setFilters, 
    selectedRows, setSelectedRows, columnValues, page, setPage, pageSize, 
    setPageSize, totalCount, totalPages, sortable, searchable, filterable, selectable
  ]);
}
